// Effective-dated + per-region rate resolution for the Plan Library (Phase 1).
//
// `plan_rates` rows are keyed by (planId, clientId, ratingAreaId, ageBand/tier,
// effectiveStart). A "version" = the rows sharing (planId, clientId, ratingAreaId,
// effectiveStart). This module selects, for a given rating region + coverage date,
// exactly the rows the premium calculator should see (it still picks age/tier).
//
// Precedence, applied in order:
//   1. client-scoped rows win over default (null-client) rows — existing behavior.
//   2. region-specific rows (matching ratingAreaId) win over region-agnostic
//      (null ratingAreaId) rows — supports manual single-region plans + library
//      plans that carry all rating regions.
//   3. the version whose [effectiveStart, effectiveEnd) window covers `asOf`
//      (null start = -infinity, null end = +infinity). If `asOf` precedes every
//      version, fall back to the earliest known version so we never return empty
//      when data exists.

export interface VersionedRateRow {
  clientId?: number | null;
  ratingAreaId?: number | null;
  effectiveStart?: string | Date | null;
  effectiveEnd?: string | Date | null;
  isActive?: boolean | null;
  [key: string]: any;
}

export interface ResolveRateOptions {
  /** Member/client rating region (e.g. CA 1..19). Omit to skip region filtering. */
  ratingAreaId?: number | null;
  /** Coverage date to resolve the version for. Defaults to now. */
  asOf?: Date | string | null;
  /** Client scope — when set, client-owned rows win over default rows. */
  clientId?: number | null;
}

function toTime(d: string | Date | null | undefined): number | null {
  if (d == null) return null;
  const t = d instanceof Date ? d.getTime() : new Date(d).getTime();
  return Number.isFinite(t) ? t : null;
}

/** A row is effective at `asOfMs` when start <= asOf < end (null start = -inf, null end = +inf). */
export function isRateEffectiveAt(row: VersionedRateRow, asOfMs: number): boolean {
  const start = toTime(row.effectiveStart ?? null);
  const end = toTime(row.effectiveEnd ?? null);
  if (start != null && asOfMs < start) return false;
  if (end != null && asOfMs >= end) return false;
  return true;
}

/**
 * Region-specific rows (matching `ratingAreaId`) win over region-agnostic
 * (null) rows; rows for OTHER regions are dropped. No region requested → rows
 * untouched. The one rule every rate consumer (versioned resolution, legacy
 * window resolution, the premium calculator) narrows by, so a multi-region
 * library plan is never priced at whichever area's row comes first (DEV-315).
 */
export function narrowRatesToRatingArea<T extends { ratingAreaId?: number | null }>(
  rows: T[],
  ratingAreaId: number | null | undefined,
): T[] {
  if (ratingAreaId == null) return rows;
  const areaRows = rows.filter((r) => r.ratingAreaId != null && Number(r.ratingAreaId) === Number(ratingAreaId));
  return areaRows.length > 0 ? areaRows : rows.filter((r) => r.ratingAreaId == null);
}

/**
 * Resolve the effective rate rows for one plan at a rating region + coverage date.
 * Returns the rows the calculator should use (still picks by age/tier itself).
 */
export function resolvePlanRates(
  rows: VersionedRateRow[] | null | undefined,
  opts: ResolveRateOptions = {},
): VersionedRateRow[] {
  let pool = (rows || []).filter((r) => r && (r.isActive == null || r.isActive === true));
  if (pool.length === 0) return [];

  // 1. client-scoped over default
  if (opts.clientId != null) {
    const clientRows = pool.filter((r) => r.clientId != null && Number(r.clientId) === Number(opts.clientId));
    pool = clientRows.length > 0 ? clientRows : pool.filter((r) => r.clientId == null);
    if (pool.length === 0) return [];
  }

  // 2. region-specific over region-agnostic
  if (opts.ratingAreaId != null) {
    pool = narrowRatesToRatingArea(pool, opts.ratingAreaId);
    if (pool.length === 0) return [];
  }

  // 3. version whose window covers asOf
  const asOfMs = toTime(opts.asOf ?? new Date()) ?? Date.now();
  const effective = pool.filter((r) => isRateEffectiveAt(r, asOfMs));
  if (effective.length > 0) return effective;

  // asOf precedes every version → fall back to the earliest known version.
  let minStart = Infinity;
  for (const r of pool) {
    const s = toTime(r.effectiveStart ?? null);
    if (s != null && s < minStart) minStart = s;
  }
  if (minStart === Infinity) return pool; // all null-start (single legacy version)
  return pool.filter((r) => (toTime(r.effectiveStart ?? null) ?? Infinity) === minStart);
}

/**
 * Distinct versions present for a plan/region scope, newest first — for the
 * builder UI and the "impact" prompt. A version key = effectiveStart (ISO date
 * string, or "" for the legacy null-start version).
 */
export function listRateVersions(rows: VersionedRateRow[] | null | undefined): Array<{
  effectiveStart: string | null;
  effectiveEnd: string | null;
  rowCount: number;
}> {
  const byStart = new Map<string, { effectiveStart: string | null; effectiveEnd: string | null; rowCount: number }>();
  for (const r of rows || []) {
    if (r.isActive === false) continue;
    const startKey = r.effectiveStart == null ? "" : String(r.effectiveStart);
    const existing = byStart.get(startKey);
    if (existing) {
      existing.rowCount += 1;
    } else {
      byStart.set(startKey, {
        effectiveStart: r.effectiveStart == null ? null : String(r.effectiveStart),
        effectiveEnd: r.effectiveEnd == null ? null : String(r.effectiveEnd),
        rowCount: 1,
      });
    }
  }
  return Array.from(byStart.values()).sort((a, b) => {
    const as = toTime(a.effectiveStart) ?? -Infinity;
    const bs = toTime(b.effectiveStart) ?? -Infinity;
    return bs - as;
  });
}
